import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const MORSE_MAP = {
  A:'.-',  B:'-...', C:'-.-.', D:'-..', E:'.', F:'..-.', G:'--.',
  H:'....', I:'..', J:'.---', K:'-.-', L:'.-..', M:'--', N:'-.',
  O:'---', P:'.--.', Q:'--.-', R:'.-.', S:'...', T:'-', U:'..-',
  V:'...-', W:'.--', X:'-..-', Y:'-.--', Z:'--..',
  '0':'-----','1':'.----','2':'..---','3':'...--','4':'....-',
  '5':'.....','6':'-....','7':'--...','8':'---..','9':'----.',
  '.':'.-.-.-',',':'--..--','?':'..--..',  '!':'-.-.--',
  '/':'-..-.','@':'.--.-.','-':'-....-',"'":'.....',
  ' ':' / ',
};

const REVERSE_MAP = {};
Object.entries(MORSE_MAP).forEach(([k, v]) => { if (k !== ' ') REVERSE_MAP[v.trim()] = k; });

function textToMorse(text) {
  return text.toUpperCase().split('').map(ch => {
    if (ch === ' ') return '/';
    return MORSE_MAP[ch] || '';
  }).filter(s => s !== '').join(' ').replace(/\s+/g, ' ').trim();
}

function morseToText(morse) {
  return morse.trim().split(/\s*\/\s*/).map(word =>
    word.trim().split(/\s+/).map(code => REVERSE_MAP[code] || (code ? '?' : '')).join('')
  ).join(' ');
}

const EXAMPLES = [
  { label: '🆘 SOS', text: 'SOS' },
  { label: '👋 HELLO', text: 'HELLO' },
  { label: '🌍 MORSE CODE', text: 'MORSE CODE' },
  { label: '🎮 WORDZINGO', text: 'WORDZINGO' },
  { label: '❤️ I LOVE YOU', text: 'I LOVE YOU' },
];

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const NUMBERS = '0123456789';

export default function MorseCode() {
  const [mode, setMode] = useState('encode');
  const [input, setInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [playing, setPlaying] = useState(false);
  const stopRef = useRef(false);

  const output = mode === 'encode' ? textToMorse(input) : morseToText(input);

  async function playMorse() {
    if (playing) { stopRef.current = true; return; }
    const morseStr = mode === 'encode' ? output : input.trim();
    if (!morseStr) return;

    setPlaying(true);
    stopRef.current = false;

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const unit = 0.07; // 70ms per dot
    const dot = unit;
    const dash = unit * 3;
    const symGap = unit;
    const letGap = unit * 3;
    const wordGap = unit * 7;

    let t = ctx.currentTime + 0.05;

    function beep(dur) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 700;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.35, t + 0.005);
      gain.gain.linearRampToValueAtTime(0.35, t + dur - 0.005);
      gain.gain.linearRampToValueAtTime(0, t + dur);
      osc.start(t);
      osc.stop(t + dur + 0.01);
    }

    const tokens = morseStr.split('');
    for (let i = 0; i < tokens.length; i++) {
      if (stopRef.current) break;
      const ch = tokens[i];
      if (ch === '.') { beep(dot); t += dot + symGap; }
      else if (ch === '-') { beep(dash); t += dash + symGap; }
      else if (ch === ' ') {
        // Check if next non-space is /
        const next = morseStr.slice(i + 1).trimStart();
        if (next.startsWith('/')) t += 0;
        else t += letGap - symGap;
      } else if (ch === '/') {
        t += wordGap;
      }
    }

    const totalMs = (t - ctx.currentTime + 0.1) * 1000;
    setTimeout(() => {
      setPlaying(false);
      ctx.close();
    }, totalMs);
  }

  function copy() {
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function switchMode() {
    const newMode = mode === 'encode' ? 'decode' : 'encode';
    setMode(newMode);
    setInput(output);
  }

  return (
    <div className="game-page">
      <PageSEO page="morse-code" />
      <div className="container" style={{ maxWidth: 820 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">📡</span>
          <h1>Morse Code Converter</h1>
          <p>Convert English text to Morse code or decode Morse back to text. Play the audio to hear the real beeps!</p>
        </div>

        {/* Mode tabs */}
        <div className="solver-card">
          <div className="d-flex gap-2 mb-4">
            {[['encode','📝 Text → Morse'],['decode','📡 Morse → Text']].map(([m, label]) => (
              <button key={m} className="btn fw-bold"
                onClick={() => { setMode(m); setInput(''); }}
                style={{
                  borderRadius: 50, flex: 1, fontFamily: 'Nunito, sans-serif', border: 'none',
                  background: mode === m ? 'var(--primary)' : 'var(--border)',
                  color: mode === m ? '#fff' : 'inherit'
                }}>{label}
              </button>
            ))}
          </div>

          <label className="fw-bold mb-2 d-block">
            {mode === 'encode'
              ? 'Enter English text to convert to Morse code:'
              : 'Enter Morse code (dots . dashes - and spaces, use / for word break):'}
          </label>
          <textarea
            className="form-control mb-1"
            rows={3}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={mode === 'encode'
              ? 'Type anything here… e.g. HELLO WORLD'
              : 'e.g. .... . .-.. .-.. --- / .-- --- .-. .-.. -..'}
            style={{ fontFamily: 'monospace', fontSize: '1rem', borderRadius: 12, letterSpacing: mode === 'decode' ? 2 : 0 }}
          />
          {mode === 'decode' && (
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
              Separate letters with spaces · Separate words with <strong>/</strong> · Example: <code>... --- ...</code> = SOS
            </p>
          )}

          <div className="d-flex gap-2 mb-4">
            <button className="btn btn-sm fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none' }}
              onClick={switchMode} disabled={!output}>⇄ Swap</button>
            <button className="btn btn-sm fw-bold"
              style={{ borderRadius: 50, background: '#fee2e2', color: '#991b1b', border: 'none' }}
              onClick={() => setInput('')}>✕ Clear</button>
          </div>

          {output && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="fw-bold mb-0">
                  {mode === 'encode' ? '📡 Morse Code Output:' : '📝 Decoded Text:'}
                </label>
                <div className="d-flex gap-2">
                  {mode === 'encode' && (
                    <button className="btn btn-sm fw-bold"
                      style={{
                        borderRadius: 50, border: 'none',
                        background: playing ? '#fef3c7' : '#4f46e5',
                        color: playing ? '#92400e' : '#fff'
                      }}
                      onClick={playMorse}>
                      {playing ? '⏹ Stop' : '▶ Play Audio'}
                    </button>
                  )}
                  <button className="btn btn-sm fw-bold"
                    style={{ borderRadius: 50, background: copied ? '#d1fae5' : 'var(--border)', border: 'none' }}
                    onClick={copy}>{copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: mode === 'decode' ? '1.3rem' : '1rem',
                letterSpacing: mode === 'encode' ? 2 : 1, lineHeight: 2,
                padding: '1rem', background: '#f8fafc', borderRadius: 12,
                wordBreak: 'break-word', color: '#4f46e5', fontWeight: 700
              }}>
                {output}
              </div>
            </div>
          )}
        </div>

        {/* Quick examples */}
        <div className="solver-card">
          <h5 className="fw-bold mb-3">⚡ Quick Examples</h5>
          <div className="d-flex flex-wrap gap-2">
            {EXAMPLES.map(({ label, text }) => (
              <button key={text} className="btn btn-sm fw-bold"
                style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito, sans-serif' }}
                onClick={() => { setMode('encode'); setInput(text); }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Reference table */}
        <div className="solver-card">
          <h5 className="fw-bold mb-3">📖 Morse Code Reference Chart</h5>
          <div className="row g-3">
            <div className="col-12 col-md-8">
              <div className="fw-bold mb-2" style={{ fontSize: '0.85rem', color: '#6b7280' }}>Letters</div>
              <div className="d-flex flex-wrap gap-2">
                {ALPHABET.split('').map(ch => (
                  <div key={ch} style={{
                    minWidth: 58, padding: '0.4rem 0.3rem', textAlign: 'center',
                    background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                    cursor: 'pointer'
                  }} onClick={() => { setMode('encode'); setInput(ch); }}>
                    <div style={{ fontWeight: 800, color: '#4f46e5', fontSize: '1rem' }}>{ch}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#374151', letterSpacing: 2 }}>{MORSE_MAP[ch]}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="fw-bold mb-2" style={{ fontSize: '0.85rem', color: '#6b7280' }}>Numbers</div>
              <div className="d-flex flex-wrap gap-2">
                {NUMBERS.split('').map(ch => (
                  <div key={ch} style={{
                    minWidth: 58, padding: '0.4rem 0.3rem', textAlign: 'center',
                    background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                    cursor: 'pointer'
                  }} onClick={() => { setMode('encode'); setInput(ch); }}>
                    <div style={{ fontWeight: 800, color: '#ea580c', fontSize: '1rem' }}>{ch}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#374151', letterSpacing: 2 }}>{MORSE_MAP[ch]}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 rounded-3" style={{ background: '#ede9fe', fontSize: '0.82rem' }}>
                <strong>Timing Guide:</strong>
                <div>· = 1 unit (dot)</div>
                <div>— = 3 units (dash)</div>
                <div>Letter gap = 3 units</div>
                <div>Word gap = 7 units</div>
              </div>
            </div>
          </div>
        </div>

        <SeoContent game="morse-code" />
      </div>
    </div>
  );
}
